import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HardHat, House, Drop, Thermometer, Wrench, Buildings, Lightning, ArrowRight, ArrowLeft, Check, CircleNotch } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useLang } from '@/context/LanguageContext';
import { SERVICES_LIST, CONSENT_TEXT, UI_TEXT } from '@/data/tradeData';
import { fireLeadEvent } from '@/components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { HardHat, House, Drop, Thermometer, Wrench, Buildings, Lightning };

function getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_content: params.get('utm_content') || '',
    utm_term: params.get('utm_term') || '',
  };
}

export default function QuoteWizard({ preselectedService, onClose, embedded }) {
  const { lang } = useLang();
  const t = UI_TEXT[lang].wizard;
  const services = SERVICES_LIST[lang];
  const consentText = CONSENT_TEXT[lang];

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    service: preselectedService || '',
    property_type: '',
    zip_code: '',
    timeline: '',
    name: '',
    phone: '',
    email: '',
    sms_consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const canNext = () => {
    if (step === 1) return !!form.service;
    if (step === 2) return !!form.property_type && !!form.zip_code && !!form.timeline;
    if (step === 3) return !!form.name && !!form.phone && !!form.email;
    return false;
  };

  const handleSubmit = async () => {
    if (!canNext()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        ...getUTMParams(),
        consent_text: consentText,
        consent_page_url: window.location.href,
        lang,
      };
      await axios.post(`${API}/leads`, payload);
      fireLeadEvent(form.service);
      setDone(true);
    } catch {
      toast.error(lang === 'es' ? 'Error. Intente de nuevo.' : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const slideVariants = {
    enter: { x: 40, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: -40, opacity: 0 },
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12 px-6">
        <div className="w-16 h-16 bg-[#E31937] rounded-full flex items-center justify-center mx-auto mb-6">
          <Check size={32} weight="bold" className="text-white" />
        </div>
        <h3 className="font-display font-black text-2xl text-white mb-3">{t.successTitle}</h3>
        <p className="text-[#A3A3A3] text-sm max-w-xs mx-auto">{t.success}</p>
      </motion.div>
    );
  }

  return (
    <div data-testid="quote-wizard" className={embedded ? '' : 'p-6'}>
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3].map(s => (
          <div key={s} className={`h-1 flex-1 transition-colors ${s <= step ? 'bg-[#E31937]' : 'bg-white/10'}`} />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="s1" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#E31937] mb-2">Step 1</p>
            <h3 className="font-display font-bold text-xl text-white mb-6">{t.step1}</h3>
            <div className="grid grid-cols-2 gap-3">
              {services.map(s => {
                const Icon = ICONS[s.icon];
                const selected = form.service === s.key;
                return (
                  <button
                    key={s.key}
                    data-testid={`wizard-service-${s.key}`}
                    onClick={() => set('service', s.key)}
                    className={`p-4 text-left border transition-all ${selected ? 'border-[#E31937] bg-[#E31937]/10' : 'border-white/10 hover:border-white/30'}`}
                  >
                    {Icon && <Icon size={24} weight="light" className={selected ? 'text-[#E31937]' : 'text-[#A3A3A3]'} />}
                    <span className={`block mt-2 text-sm font-semibold ${selected ? 'text-white' : 'text-[#A3A3A3]'}`}>{s.label}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#E31937] mb-2">Step 2</p>
            <h3 className="font-display font-bold text-xl text-white mb-6">{t.step2}</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {['own', 'rent'].map(v => (
                  <button key={v} data-testid={`wizard-${v}`} onClick={() => set('property_type', v)}
                    className={`p-4 border text-sm font-semibold transition-all ${form.property_type === v ? 'border-[#E31937] bg-[#E31937]/10 text-white' : 'border-white/10 text-[#A3A3A3] hover:border-white/30'}`}>
                    {t[v]}
                  </button>
                ))}
              </div>
              <input data-testid="wizard-zip" placeholder={t.zip} value={form.zip_code} onChange={e => set('zip_code', e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white placeholder:text-[#666] px-4 py-3 text-sm focus:border-[#E31937] focus:outline-none" />
              <div>
                <p className="text-xs text-[#A3A3A3] mb-2 uppercase tracking-wider">{t.timeline}</p>
                <div className="grid grid-cols-3 gap-2">
                  {[{ k: 'asap', l: t.asap }, { k: '1-3months', l: t.months }, { k: 'researching', l: t.researching }].map(({ k, l }) => (
                    <button key={k} data-testid={`wizard-timeline-${k}`} onClick={() => set('timeline', k)}
                      className={`p-3 border text-xs font-semibold transition-all ${form.timeline === k ? 'border-[#E31937] bg-[#E31937]/10 text-white' : 'border-white/10 text-[#A3A3A3] hover:border-white/30'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="s3" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#E31937] mb-2">Step 3</p>
            <h3 className="font-display font-bold text-xl text-white mb-6">{t.step3}</h3>
            <div className="space-y-4">
              <input data-testid="wizard-name" placeholder={t.name} value={form.name} onChange={e => set('name', e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white placeholder:text-[#666] px-4 py-3 text-sm focus:border-[#E31937] focus:outline-none" />
              <input data-testid="wizard-phone" placeholder={t.phone} value={form.phone} onChange={e => set('phone', e.target.value)} type="tel"
                className="w-full bg-white/5 border border-white/10 text-white placeholder:text-[#666] px-4 py-3 text-sm focus:border-[#E31937] focus:outline-none" />
              <input data-testid="wizard-email" placeholder={t.email} value={form.email} onChange={e => set('email', e.target.value)} type="email"
                className="w-full bg-white/5 border border-white/10 text-white placeholder:text-[#666] px-4 py-3 text-sm focus:border-[#E31937] focus:outline-none" />
              <div className="flex items-start gap-3">
                <input type="checkbox" id="wiz-consent" data-testid="wizard-consent" checked={form.sms_consent} onChange={e => set('sms_consent', e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-[#E31937] cursor-pointer" />
                <label htmlFor="wiz-consent" className="text-[10px] text-[#666] leading-relaxed cursor-pointer">
                  {consentText}{' '}
                  <Link to={lang === 'es' ? '/es/terminos' : '/terms'} className="text-[#E31937] underline">Terms</Link>
                  {' & '}
                  <Link to={lang === 'es' ? '/es/privacidad' : '/privacy'} className="text-[#E31937] underline">Privacy</Link>.
                </label>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 gap-3">
        {step > 1 ? (
          <button data-testid="wizard-back" onClick={() => setStep(s => s - 1)} className="flex items-center gap-2 text-[#A3A3A3] hover:text-white text-sm transition-colors">
            <ArrowLeft size={16} /> {t.back}
          </button>
        ) : <div />}
        {step < 3 ? (
          <button data-testid="wizard-next" onClick={() => canNext() && setStep(s => s + 1)} disabled={!canNext()}
            className="bg-[#E31937] text-white font-bold uppercase tracking-wider px-6 py-3 text-sm hover:bg-[#FF2445] transition-colors disabled:opacity-30 flex items-center gap-2">
            {t.next} <ArrowRight size={16} />
          </button>
        ) : (
          <button data-testid="wizard-submit" onClick={handleSubmit} disabled={!canNext() || loading}
            className="bg-[#E31937] text-white font-bold uppercase tracking-wider px-6 py-3 text-sm hover:bg-[#FF2445] transition-colors disabled:opacity-30 flex items-center gap-2">
            {loading ? <CircleNotch size={16} className="animate-spin" /> : null}
            {t.submit}
          </button>
        )}
      </div>
    </div>
  );
}
